/**
 * Lazy controller registry — import a controller's chunk the first time its identifier appears in
 * `data-controller`, then register it with Stimulus. ONLY for large single-page controllers.
 *
 *   registerLazy(application, {
 *     'client-card': () => import(/* webpackChunkName: "c-client-card" *\/ '../controllers/client_card_controller.js'),
 *   })
 *
 * The map is static: an import path is NEVER derived from markup or data. On failure the affected
 * elements get `data-controller-error="<identifier>"`, the window gets `ls:controller-load-failed`
 * ({ identifier }; the shell shows a reload notice), and the failed attempt is forgotten so the next
 * appearance (or `load()`) retries. No automatic page reload: that policy belongs to the shell.
 *
 * @param {import('@hotwired/stimulus').Application} application
 * @param {Record<string, () => Promise<{ default: Function }>>} loaders identifier -> import()
 * @param {{ root?: Element, onError?: (identifier: string, error: unknown) => void }} [options]
 */
import { emitApp } from './events.js'

export function registerLazy(application, loaders, { root = document.documentElement, onError = () => {} } = {}) {
  /** @type {Map<string, Promise<boolean>>} */
  const loading = new Map()
  const users = (identifier) => root.querySelectorAll(`[data-controller~="${identifier}"]`)

  /** @param {string} identifier @returns {Promise<boolean>} whether the controller is registered */
  function load(identifier) {
    if (!Object.hasOwn(loaders, identifier)) return Promise.resolve(false)
    let promise = loading.get(identifier)
    if (promise) return promise
    promise = Promise.resolve()
      .then(() => loaders[identifier]())
      .then(
        (module) => {
          application.register(identifier, module.default)
          for (const element of users(identifier)) element.removeAttribute('data-controller-error')
          return true
        },
        (error) => {
          loading.delete(identifier) // the next appearance, or load(), tries again
          for (const element of users(identifier)) element.setAttribute('data-controller-error', identifier)
          emitApp('controller-load-failed', { identifier })
          onError(identifier, error)
          return false
        },
      )
    loading.set(identifier, promise)
    return promise
  }

  /** @param {Node} node */
  function scan(node) {
    if (!(node instanceof Element)) return
    for (const element of [node, ...node.querySelectorAll('[data-controller]')]) {
      for (const identifier of (element.getAttribute('data-controller') ?? '').split(/\s+/)) {
        if (identifier) load(identifier)
      }
    }
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') scan(record.target)
      else record.addedNodes.forEach(scan)
    }
  })
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-controller'] })
  scan(root)

  return { load, stop: () => observer.disconnect() }
}
